// Iqamah settings: for each prayer, a delay after the athan or a fixed time.
// Saved as { delay: "MM:SS", isFixed: false } or { delay: "HH:MM", isFixed: true }.
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { HiOutlineLightBulb } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import BackButton from "../../../components/buttons/BackButton";
import Button from "../../../components/buttons/button";
import { useGetiqamahtimesQuery, useUpdateiqamahdelayMutation } from "../../../store/Reducer/prayertimes";
import { athanOf } from "../user-settings/prayer-time-table";
import useMasjidFormat from "../../../hooks/useMasjidFormat";

const PRAYERS = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];

const pad = (n) => String(n).padStart(2, "0");
const toForm = (setting) => {
  const [a, b] = String(setting?.delay || "0:0").split(":").map(Number);
  return setting?.isFixed
    ? { mode: "fixed", minutes: "10", time: `${pad(a || 0)}:${pad(b || 0)}` }
    : { mode: "delay", minutes: String(a || 0), time: "" };
};
const addMinutes = (hhmm, minutes) => {
  const [h, m] = hhmm.split(":").map(Number);
  const total = (h * 60 + m + Number(minutes || 0)) % 1440;
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
};

export default function PrayerTimeIqamahCalculation() {
  // Times shown in the masjid's format; the form itself uses 24-hour values.
  const fmt = useMasjidFormat();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const now = new Date();
  const { data, isLoading: loadingSaved, error } = useGetiqamahtimesQuery({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [updateiqamahdelay, { isLoading }] = useUpdateiqamahdelayMutation();
  const [form, setForm] = useState(null);

  // Start from what the masjid has saved.
  useEffect(() => {
    if (data?.data?.delays && !form) {
      setForm(Object.fromEntries(PRAYERS.map((p) => [p, toForm(data.data.delays[p])])));
    }
  }, [data, form]);
  useEffect(() => {
    if (error) toast.error(error?.data?.message || "Couldn't load your iqamah settings.");
  }, [error]);

  // Today's athan, to show what each setting gives today.
  const today = useMemo(() => {
    const key = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`;
    return (data?.data?.iqamahTimes || []).find((d) => d.date?.gregorian?.date === key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const set = (prayer, patch) => setForm((f) => ({ ...f, [prayer]: { ...f[prayer], ...patch } }));

  const problems = form
    ? PRAYERS.filter((p) => {
        const f = form[p];
        if (f.mode === "fixed") return !/^\d{2}:\d{2}$/.test(f.time);
        const n = Number(f.minutes);
        return f.minutes === "" || !Number.isInteger(n) || n < 0 || n > 180;
      })
    : [];

  const handleSave = async () => {
    if (problems.length) {
      toast.error(`Check the time for ${problems.join(", ")}: minutes must be 0 to 180, or pick a fixed time.`);
      return;
    }
    const delays = Object.fromEntries(
      PRAYERS.map((p) => {
        const f = form[p];
        return [p, f.mode === "fixed" ? { delay: f.time, isFixed: true } : { delay: `${pad(Number(f.minutes))}:00`, isFixed: false }];
      })
    );
    try {
      await updateiqamahdelay({ delays }).unwrap();
      toast.success("Iqamah times saved");
      navigate("/user-dashboard/prayer-times/iqamah");
    } catch (err) {
      toast.error(err?.data?.message || "Couldn't save the iqamah times.");
    }
  };

  return (
    <div className="w-full mx-auto p-6 space-y-6">
      <h1 className="text-2xl text-start font-semibold text-slate-800 flex items-center gap-2">
        <BackButton /> Iqamah times
      </h1>
      <p className="py-3 px-3 text-start text-md flex items-center gap-x-2 border border-blue-500 bg-blue-50 text-blue-700 rounded-lg">
        <HiOutlineLightBulb size={22} className="shrink-0" />
        Set each iqamah a number of minutes after the athan, or at a fixed time. A change is announced on your screens and website for 24 hours.
      </p>

      {!form ? (
        <p className="text-gray-500">{loadingSaved ? "Loading your iqamah settings…" : "Couldn't load your iqamah settings."}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PRAYERS.map((prayer) => {
            const f = form[prayer];
            const athan = athanOf(today, prayer);
            const result = f.mode === "fixed" ? f.time : athan && f.minutes !== "" ? addMinutes(athan, f.minutes) : "";
            const bad = problems.includes(prayer);
            return (
              <fieldset key={prayer} className="rounded-xl border border-gray-200 bg-white p-4">
                <legend className="px-1 text-lg font-medium text-gray-800">{prayer}</legend>
                <div className="flex gap-2" role="radiogroup" aria-label={`${prayer} iqamah type`}>
                  {[["delay", "After the athan"], ["fixed", "Fixed time"]].map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      role="radio"
                      aria-checked={f.mode === mode}
                      onClick={() => set(prayer, { mode })}
                      className={`flex-1 py-2 px-3 rounded-md text-sm border transition-colors ${
                        f.mode === mode ? "bg-blue-50 text-blue-700 border-blue-500 font-medium" : "bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-3">
                  {f.mode === "fixed" ? (
                    <input
                      type="time"
                      aria-label={`${prayer} fixed iqamah time`}
                      value={f.time}
                      onChange={(e) => set(prayer, { time: e.target.value })}
                      className={`w-36 rounded-md border px-3 py-2 text-lg ${bad ? "border-red-500" : "border-gray-300"}`}
                    />
                  ) : (
                    <>
                      <input
                        type="number"
                        min="0"
                        max="180"
                        aria-label={`${prayer} minutes after athan`}
                        value={f.minutes}
                        onChange={(e) => set(prayer, { minutes: e.target.value })}
                        className={`w-24 rounded-md border px-3 py-2 text-lg text-center ${bad ? "border-red-500" : "border-gray-300"}`}
                      />
                      <span className="text-sm text-gray-600">minutes after the athan</span>
                    </>
                  )}
                </div>
                {athan && result && (
                  <p className="mt-3 text-sm text-gray-500">
                    Today: athan {fmt.time(athan)}, iqamah <span className="font-semibold text-emerald-700">{fmt.time(result)}</span>
                    {f.mode === "fixed" && result < athan && <span className="block text-amber-700">This is before today's athan.</span>}
                  </p>
                )}
              </fieldset>
            );
          })}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <Button text="Cancel" type="secondary" className="w-32" onClick={() => navigate(-1)} />
        <Button text={t("save")} type="primary" className="w-32" onClick={handleSave} loading={isLoading} disabled={!form} />
      </div>
    </div>
  );
}
